import React, { useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field } from './DialogShell';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, Palette, AlertTriangle, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { extractPaletteCandidates, paletteVariants, schemeFromPalette } from '../../lib/paletteExtract';
import { SCHEME_PRESETS, contrastText, ensureSchemeRegistered } from '../../lib/colors';
import { recolorProject } from '../../lib/templates';
import { v4 as uuidv4 } from 'uuid';

const findImage = (state, objectId) => {
  const all = [];
  state.pages.forEach((pg) => (pg.objects || []).forEach((o) => all.push(o)));
  Object.values(state.masters || {}).forEach((m) => (m.objects || []).forEach((o) => all.push(o)));
  const walk = (list) => { for (const o of list) { if (o.id === objectId) return o; if (o.children) { const c = walk(o.children); if (c) return c; } } return null; };
  return walk(all);
};

/**
 * Sampled Palette Builder: the picture's colours → a few five-colour suggestions (Dominant / Vivid / Soft / Contrast) → a
 * Publisher-style colour scheme for this publication. "Create scheme" adds it to the publication's schemes and makes it the
 * active one (colour pickers, WordArt, building blocks use it); "Create & recolour" also maps the current scheme's colours in
 * every object to the new ones (one undoable step).
 */
export const PaletteFromPictureDialog = ({ props }) => {
  const { state, dispatch, closeDialog } = useEditor();
  const obj = props.objectId ? findImage(state, props.objectId) : null;
  const src = props.src || obj?.properties?.src || '';
  const [variants, setVariants] = useState(null); // [{ id, label, hint, colors }]
  const [mode, setMode] = useState('dominant');
  const [error, setError] = useState(null);
  const [name, setName] = useState(props.name || 'From picture');

  useEffect(() => {
    if (!src) { setError('Select a picture first.'); return undefined; }
    let cancelled = false;
    setVariants(null); setError(null);
    extractPaletteCandidates(src)
      .then((cands) => {
        if (cancelled) return;
        const v = paletteVariants(cands, 5);
        if (!v.length) setError('No usable colours were found in this picture.');
        else { setVariants(v); setMode((m) => (v.some((x) => x.id === m) ? m : v[0].id)); }
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'The picture could not be read.'); });
    return () => { cancelled = true; };
  }, [src]);

  const current = variants?.find((v) => v.id === mode) || variants?.[0] || null;
  const colors = current?.colors || null;
  const scheme = useMemo(() => (colors ? schemeFromPalette(colors, name.trim() || 'From picture') : null), [colors, name]);

  const create = (recolor) => {
    if (!scheme) return;
    const fromId = state.schemeId;
    const previous = SCHEME_PRESETS[fromId];
    const id = `custom:${uuidv4()}`;
    let payload = null;
    if (recolor && previous) {
      // Map the current scheme (its eight colours, tints / shades and derived palette values) onto the new one.
      const tempId = '__previous_scheme__';
      SCHEME_PRESETS[tempId] = previous.map((c) => ({ ...c }));
      ensureSchemeRegistered({ id, colors: scheme.colors });
      const next = recolorProject({ pages: state.pages, masters: state.masters }, tempId, id);
      delete SCHEME_PRESETS[tempId];
      if (next.pages !== state.pages || next.masters !== state.masters) payload = { pages: next.pages, masters: next.masters };
    }
    dispatch({ type: 'ADD_CUSTOM_SCHEME', payload: { id, name: scheme.name, colors: scheme.colors } });
    if (payload) dispatch({ type: 'REPLACE_DOCUMENT_CONTENT', payload });
    toast.success(`Colour scheme “${scheme.name}” created${payload ? ' and applied to the publication' : ''}`, {
      description: payload ? 'Every object that used the previous scheme colours now uses the new ones. Ctrl+Z undoes the recolouring.' : 'It is now the active scheme — colour pickers, WordArt and building blocks use it. Saved with this publication; the ⋯ on its chip in Page Design ▸ Schemes edits its colours, renames, duplicates or deletes it.',
      icon: payload ? <Undo2 className="h-4 w-4" /> : <Palette className="h-4 w-4" />,
    });
    closeDialog();
  };

  return (
    <WpDialog
      name="palette-from-picture"
      title="Colour Scheme from Picture"
      description="Sample the picture's colours, pick the palette you like and turn it into a colour scheme for this publication."
      width="w-[600px]"
      onCancel={closeDialog}
      onOk={() => create(false)}
      okLabel="Create Scheme"
      okDisabled={!scheme}
      extraFooter={<Button variant="outline" disabled={!scheme} onClick={() => create(true)} data-testid="palette-create-recolor-button">Create & Recolour Publication</Button>}
    >
      <div className="space-y-4" data-testid="palette-from-picture">
        <div className="flex gap-4">
          <div className="h-24 w-32 shrink-0 rounded-md border border-border bg-muted/40 overflow-hidden flex items-center justify-center">
            {src ? <img src={src} alt="" className="h-full w-full object-cover" data-testid="palette-source-image" /> : <Palette className="h-6 w-6 text-muted-foreground" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground mb-1.5">Palette suggestions</div>
            {error ? (
              <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs" role="alert" data-testid="palette-error"><AlertTriangle className="h-3.5 w-3.5 text-destructive" /> {error}</div>
            ) : !variants ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground h-16" data-testid="palette-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sampling the picture…</div>
            ) : (
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Palette suggestions" data-testid="palette-mode">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={mode === v.id}
                    onClick={() => setMode(v.id)}
                    title={v.hint}
                    className={`rounded-md border p-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${mode === v.id ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : 'border-border hover:bg-muted'}`}
                    data-testid={`palette-mode-${v.id}`}
                  >
                    <div className="flex gap-0.5 mb-1">{v.colors.map((c, i) => <span key={`${c.hex}-${i}`} className="h-4 flex-1 rounded-[2px]" style={{ background: c.hex }} />)}</div>
                    <div className="text-[11px] font-medium text-foreground leading-tight">{v.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {colors && (
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">{current.label} — {current.hint}</div>
            <div className="grid grid-cols-5 gap-2" data-testid="palette-swatches">
              {colors.map((c, i) => (
                <div key={`${c.hex}-${i}`} className="rounded-md border border-border overflow-hidden" data-testid={`palette-swatch-${i}`} data-hex={c.hex}>
                  <div className="h-12 flex items-end justify-end p-1" style={{ background: c.hex }}>{c.share > 0 && <span className="text-[10px] font-mono rounded px-1" style={{ color: contrastText(c.hex), background: 'rgba(0,0,0,0.12)' }}>{Math.max(1, Math.round(c.share * 100))}%</span>}</div>
                  <div className="px-1.5 py-1 text-[11px] font-mono text-foreground bg-card">{c.hex.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {scheme && (
          <div className="space-y-2">
            <Field label="Scheme name"><Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 bg-background border-border" data-testid="palette-scheme-name" /></Field>
            <div className="text-xs text-muted-foreground">Resulting scheme</div>
            <div className="flex gap-1" data-testid="palette-scheme-preview">
              {scheme.colors.map((c, i) => (
                <div key={`${c.hex}-${i}`} className="flex-1 min-w-0" title={`${c.name}: ${c.hex}`}>
                  <div className="h-8 rounded-sm border border-black/10 dark:border-white/15" style={{ background: c.hex }} />
                  <div className="mt-0.5 text-[9px] leading-tight text-muted-foreground truncate">{c.name.replace(', Background 1', '').replace(', Text 1', '')}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Text 1 stays black and Background 1 white, like every Publisher scheme; the five colours become Accent 1–5 and Text 2 is a dark shade of the leading colour. “Create & Recolour Publication” also swaps the current scheme's colours wherever they are used (undoable).</p>
          </div>
        )}
      </div>
    </WpDialog>
  );
};

/** Rename one of the publication's own colour schemes (Page Design ▸ Schemes ▸ chip menu ▸ Rename…). */
export const RenameSchemeDialog = ({ props }) => {
  const { state, dispatch, closeDialog } = useEditor();
  const scheme = state.customSchemes.find((c) => c.id === props.id);
  const [name, setName] = useState(scheme?.name || '');
  const ok = () => {
    const n = name.trim();
    if (!scheme || !n) return;
    dispatch({ type: 'UPDATE_CUSTOM_SCHEME', payload: { id: scheme.id, name: n } });
    toast.success(`Colour scheme renamed to “${n}”`);
    closeDialog();
  };
  return (
    <WpDialog name="rename-scheme" title="Rename Colour Scheme" width="w-[400px]" onCancel={closeDialog} onOk={ok} okLabel="Rename" okDisabled={!scheme || !name.trim()}>
      {scheme ? (
        <div className="space-y-3">
          <div className="flex gap-0.5">{scheme.colors.slice(1, 7).map((c, i) => <span key={`${c.hex}-${i}`} className="h-5 flex-1 rounded-[2px]" style={{ background: c.hex }} />)}</div>
          <Field label="Name"><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ok(); }} className="h-8 bg-background border-border" data-testid="rename-scheme-input" /></Field>
        </div>
      ) : <p className="text-sm text-muted-foreground">This colour scheme no longer exists.</p>}
    </WpDialog>
  );
};
